import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
    postCall,
    deleteCall,
    getById,
    BASE_URL,
    getCall,
    putCall,
} from "../../utils/api";
import axios from "axios";
import toast from "react-hot-toast";

const initialState = {
    data: {
        data: [],
        pagination: null,
    },
    topPickDetail: null,
    error: null,
    status: "idle",
    isUpdateStatus: "idle",
    updateStatus: "idle"
};

export const getTopPick = createAsyncThunk(
    "topPick/get",
    async ({ query = "", page = 1, per_page = 50 }) => {
        try {
            const response = await getCall(
                `/admin/v1/top-pick-v1.0.26?query=${query}&page=${page}&per_page=${per_page}`,
                false
            );
            return response;
        } catch (error) {
            return Promise.reject(error);
        }
    }
);

export const getTopPickById = createAsyncThunk(
    "topPick/getById",
    async (id) => {
        try {
            const response = await getById(`/admin/v1/top-pick`, id, false);
            return response;
        } catch (error) {
            toast.error(error?.response?.data.message);
            return Promise.reject(error);
        }
    }
);

export const addTopPick = createAsyncThunk(
    "topPick/add",
    async (data) => {
        try {
            const response = await postCall("/admin/v1/top-pick", data);
            return response;
        } catch (error) {
            toast.error(error?.response?.data.message);
            return Promise.reject(error);
        }
    }
);

export const updateTopPick = createAsyncThunk(
    "topPick/update",
    async ({ id, formData }, { rejectWithValue }) => {
        const token = JSON.parse(localStorage.getItem("token"));
        try {
            const response = await axios.post(
                `${BASE_URL}/admin/v1/top-pick/${id}`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            toast.success(
                response.data.message || "Top pick updated successfully!"
            );
            return response.data;
        } catch (error) {
            toast.error(error?.response?.data.message);
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const deleteTopPick = createAsyncThunk(
    "topPick/delete",
    async (id) => {
        try {
            await deleteCall(`/admin/v1/top-pick/${id}`);
            return id;
        } catch (error) {
            toast.error(error?.response?.data.message);
            return Promise.reject(error);
        }
    }
);

export const dragDropTopPick = createAsyncThunk(
    "topPick/dragDrop",
    async ({ id, to }) => {
        try {
            const response = await postCall(
                `/admin/v1/top-pick/${id}/direction`,
                {
                    to,
                }
            );
            return response;
        } catch (error) {
            toast.error(error?.response?.data.message);
            return Promise.reject(error);
        }
    }
);

export const updateTopPickTitle = createAsyncThunk(
    'topPick/updateTitle',
    async ({ display_name }, { rejectWithValue }) => {
        try {
            const response = await putCall('/admin/v1/top-pick', {
                type: 'top_pick',
                display_name
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to update title');
        }
    }
);

const topPickSlice = createSlice({
    name: "topPick",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getTopPick.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(getTopPick.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.data = action.payload;
            })
            .addCase(getTopPick.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message || "Failed to fetch data.";
            })

            .addCase(getTopPickById.pending, (state) => {
                state.status = "loading";
            })
            .addCase(getTopPickById.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.topPickDetail = action.payload;
            })
            .addCase(getTopPickById.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message || "Failed to fetch detail.";
            })

            .addCase(addTopPick.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(addTopPick.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.data.data.push(action.payload);
            })
            .addCase(addTopPick.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message || "Failed to add item.";
            })

            .addCase(updateTopPick.pending, (state) => {
                state.isUpdateStatus = "loading";
                state.error = null;
            })
            .addCase(updateTopPick.fulfilled, (state, action) => {
                state.isUpdateStatus = "succeeded";
                const index = state.data.data.findIndex(
                    (item) => item.id === action.payload.data.id
                );
                if (index !== -1) {
                    state.data.data[index] = action.payload.data;
                }
            })
            .addCase(updateTopPick.rejected, (state, action) => {
                state.isUpdateStatus = "failed";
                state.error = action.payload || "Failed to update item.";
            })

            .addCase(deleteTopPick.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(deleteTopPick.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.data.data = state.data.data.filter(
                    (item) => item.id !== action.payload
                );
            })
            .addCase(deleteTopPick.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message || "Failed to delete item.";
            })

            .addCase(dragDropTopPick.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(dragDropTopPick.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.data.data = action.payload.data;
            })
            .addCase(dragDropTopPick.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message || "Failed to drag and drop item.";
            })
            .addCase(updateTopPickTitle.pending, (state) => {
                state.updateStatus = 'loading';
            })
            .addCase(updateTopPickTitle.fulfilled, (state, action) => {
                state.updateStatus = 'succeeded';
                if (state.data) {
                    state.data.display_name = action.payload.display_name;
                }
            })
            .addCase(updateTopPickTitle.rejected, (state, action) => {
                state.updateStatus = 'failed';
                state.error = action.payload;
            });
    },
});

export default topPickSlice.reducer;
